"use client";

import { Calendar, User } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 px-4">
        <div className="h-12 w-3/4 rounded-md bg-muted animate-pulse" />
        <div className="h-6 w-1/2 rounded-md bg-muted animate-pulse" />
        <div className="aspect-[16/9] w-full rounded-lg bg-muted animate-pulse" />
        <div className="space-y-3">
          <div className="h-4 w-full rounded-md bg-muted animate-pulse" />
          <div className="h-4 w-full rounded-md bg-muted animate-pulse" />
          <div className="h-4 w-5/6 rounded-md bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 text-center">
        <p className="text-muted-foreground">Failed to load insight.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 text-center">
        <p className="text-muted-foreground">Insight not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const relatedPosts = posts.slice(1, 4);

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      <article>
        <header className="mb-8 space-y-6 text-center">
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2">
              {post.categories.map((cat, i) => (
                <Link
                  key={cat.id}
                  data-index={i}
                  to={cat.link || "#"}
                  className="text-primary-foreground inline-flex items-center px-3 py-1 text-xs font-medium uppercase tracking-wider bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          )}

          <h1 className="font-default font-serif text-3xl font-semibold leading-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {post.author && (
              <div className="flex items-center gap-2">
                {post.author.avatar && post.author.avatar["48"] ? (
                  <img
                    src={post.author.avatar["48"]}
                    alt={post.author.name}
                    className="h-8 w-8 rounded-lg object-cover"
                    loading="lazy"
                  />
                ) : (
                  <User className="h-4 w-4" />
                )}
                <span>{post.author.name}</span>
              </div>
            )}
            {formattedDate && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <time dateTime={post.date}>{formattedDate}</time>
              </div>
            )}
          </div>
        </header>

        {post.featuredImage && (
          <div className="mb-10 overflow-hidden rounded-lg shadow-lg">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="aspect-[16/9] w-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        {post.content && (
          <div
            className="prose prose-lg mx-auto max-w-none font-default text-foreground [&_p]:mb-6 [&_p]:leading-relaxed [&_p]:text-base sm:[&_p]:text-lg [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-default [&_h2]:font-serif [&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-default [&_h3]:font-serif [&_h3]:text-xl sm:[&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:opacity-80 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul_li]:mb-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol_li]:mb-2 [&_img]:rounded-lg [&_img]:my-6 [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded-md [&_code]:text-sm [&_code]:font-mono [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-6 [&_strong]:text-foreground [&_b]:text-foreground [&_strong]:font-semibold [&_b]:font-semibold"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        )}

        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-border pt-8">
            <span className="text-sm font-medium text-muted-foreground">Topics:</span>
            {post.tags.map((tag, i) => (
              <Link
                key={tag.id}
                data-index={i}
                to={tag.link || "#"}
                className="text-foreground inline-flex items-center px-3 py-1 text-xs font-medium bg-secondary rounded-lg hover:bg-secondary/80 transition-colors"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        )}

        {post.author && (
          <Card className="mt-12 bg-card border-border">
            <CardContent className="flex flex-col items-center gap-4 p-6 text-center sm:flex-row sm:text-left">
              {post.author.avatar && post.author.avatar["96"] && (
                <img
                  src={post.author.avatar["96"]}
                  alt={post.author.name}
                  className="h-20 w-20 rounded-lg object-cover shrink-0"
                  loading="lazy"
                />
              )}
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  Author
                </p>
                <h3 className="font-default font-serif text-xl font-semibold text-foreground">
                  {post.author.name}
                </h3>
                {post.author.link && (
                  <Link
                    to={post.author.link}
                    className="text-primary mt-2 inline-block text-sm underline-offset-4 hover:underline"
                  >
                    View all insights
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </article>

      {relatedPosts.length > 0 && (
        <aside className="mt-16 border-t border-border pt-12">
          <h2 className="font-default font-serif text-2xl font-semibold text-foreground sm:text-3xl mb-8 text-center">
            More from Resseti
          </h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedPosts.map((rp, i) => (
              <Card key={rp.id} data-index={i} className="overflow-hidden bg-card border-border hover:shadow-lg transition-shadow">
                {rp.featuredImage && (
                  <Link to={rp.link || "#"} className="block overflow-hidden">
                    <img
                      src={rp.featuredImage}
                      alt={rp.title}
                      className="aspect-[4/3] w-full object-cover transition-transform hover:scale-105"
                      loading="lazy"
                    />
                  </Link>
                )}
                <CardContent className="space-y-3">
                  {rp.date && (
                    <time
                      dateTime={rp.date}
                      className="text-xs uppercase tracking-wider text-muted-foreground"
                    >
                      {new Date(rp.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </time>
                  )}
                  <h3 className="font-default font-serif text-lg font-semibold text-foreground line-clamp-2">
                    <Link to={rp.link || "#"} className="text-foreground hover:text-primary transition-colors">
                      {rp.title}
                    </Link>
                  </h3>
                  {rp.excerpt && (
                    <div
                      className="text-sm text-muted-foreground line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: rp.excerpt }}
                    />
                  )}
                  <Link to={rp.link || "#"}>
                    <Button variant="link" className="px-0 h-auto text-primary">
                      Read more →
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}

export default function Insight() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="3580" id="single-post-template-section" className="bg-background py-16 sm:py-20 lg:py-24">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}